import React, { useEffect, useState } from "react";
import { Modal } from "@tw/tw-common-js";
import { GoogleLogin } from "@react-oauth/google";
import api from "../../services/api";
import { useAuth } from "../../context/useAuth";
import { formatDate } from "../../services/utils";
import Note from "../common/Note";

export default function MyProfilePerformance() {
  const { user } = useAuth();
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAckModal, setShowAckModal] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [profileImgMap, setProfileImgMap] = useState({});

  useEffect(() => {
    async function fetchPerformance() {
      if (!user?._employee) return;
      setLoading(true);
      setError(null);
      const res = await api.getLatestPerformance();
      if (res.status === 200 && res.data) {
        setPerformance(res.data);
        setAcknowledged(!!res.data.acknowledgedDate);
      } else {
        setError(res.data?.error || "No performance reviews available");
      }
      setLoading(false);
    }
    fetchPerformance();
  }, [user?._employee]);

  useEffect(() => {
    if (!performance?.notes || performance.notes.length === 0) return;

    const uniqueHrCodes = {};
    performance.notes.forEach((note) => {
      if (note.creatorHRcode && !uniqueHrCodes[note.creatorHRcode]) {
        uniqueHrCodes[note.creatorHRcode] = true;
      }
    });

    const fetchImages = async () => {
      const imgUrlMap = {};
      await Promise.all(
        Object.keys(uniqueHrCodes).map(async (hrCode) => {
          const res = await api.getProfileImgThumb(hrCode);
          if (res.status === 200 && res.data) {
            imgUrlMap[hrCode] = URL.createObjectURL(res.data);
          }
        })
      );
      setProfileImgMap(imgUrlMap);
    };

    fetchImages();
  }, [performance?.notes]);

  if (loading) {
    return (
      <div className="white-box">
        <div className="content-center">
          <div className="my-profile-empty-state">Loading performance review...</div>
        </div>
      </div>
    );
  }
  if (error || !performance) {
    return (
      <div className="white-box">
        <div className="content-center">
          <div className="my-profile-empty-state">{error || "No performance reviews available"}</div>
        </div>
      </div>
    );
  }

  const criteria = [
    {
      key: "jobKnowledge",
      label: "Job Knowledge and Effectiveness",
      description:
        "Assessing the employee’s understanding of their role and their ability to perform job-specific tasks, including the accuracy and thoroughness of their work.",
    },
    {
      key: "productivity",
      label: "Productivity and Efficiency",
      description:
        "Measuring the employee’s output and how effectively they manage their time and resources; evaluating their consistency in meeting deadlines and maintaining a reliable presence at work.",
    },
    {
      key: "communication",
      label: "Communication Skills",
      description:
        "Reviewing how well the employee communicates with colleagues, managers, and clients, ensuring clear and effective exchanges of information.",
    },
    {
      key: "teamwork",
      label: "Teamwork and Collaboration",
      description:
        "Assessing the employee’s ability to work effectively within a team, contribute to collective goals, and adapt to changes while handling unexpected challenges.",
    },
    {
      key: "initiative",
      label: "Initiative and Innovation",
      description:
        "Evaluating the employee’s willingness to take on new tasks, generate new ideas, and develop solutions to problems, alongside their capability to identify issues and make sound decisions.",
    },
    {
      key: "leadership",
      label: "Leadership and Management Skills (if applicable)",
      description:
        "For employees in supervisory roles, assessing their ability to lead, manage, and develop their team effectively while fostering a collaborative work environment.",
    },
  ];

  // Format date
  function formatMonthYear(perf) {
    if (perf.month && perf.year) {
      const monthNames = {
        jan: "January",
        feb: "February",
        mar: "March",
        apr: "April",
        may: "May",
        jun: "June",
        jul: "July",
        aug: "August",
        sep: "September",
        oct: "October",
        nov: "November",
        dec: "December",
      };
      return `${monthNames[perf.month] || perf.month} ${perf.year}`;
    }
    if (perf.quarter && perf.quarter.year && perf.quarter.quarter) {
      const monthNames = {
        jan: "January",
        feb: "February",
        mar: "March",
        apr: "April",
        may: "May",
        jun: "June",
        jul: "July",
        aug: "August",
        sep: "September",
        oct: "October",
        nov: "November",
        dec: "December",
      };
      return `${monthNames[perf.quarter.quarter] || perf.quarter.quarter} ${perf.quarter.year}`;
    }
    return null;
  }

  return (
    <div className="performance-layout">
      <div className="white-box performance-header mb-10">
        <div>
          {formatMonthYear(performance) && <span>Review Period: {formatMonthYear(performance)}</span>}
          {performance.editDate && (
            <span>
              {formatMonthYear(performance) ? " - " : ""}
              Last Modified {formatDate(performance.editDate, true)}
            </span>
          )}
        </div>
        {!acknowledged && (
          <button className="blue ack-btn" onClick={() => setShowAckModal(true)}>
            Confirm I have Read This
          </button>
        )}
        {acknowledged && (
          <div className="acknowledged-msg">
            <i className="material-icons">check_circle</i> Acknowledged on {formatDate(performance.acknowledgedDate, true)}
          </div>
        )}
      </div>
      <div className="white-box criteria-section mb-10">
        <div className="criteria-table-header">
          <div className="criteria-title">Assessment Criteria</div>
          <div className="criteria-rate-title mb-10">Rate</div>
        </div>
        <div className="criteria-rate-labels">
          {[
            "Does Not Meet Expectations",
            "Partially Meets Expectations",
            "Successfully Meets Expectations",
            "Exceeds Expectations",
            "Greatly Exceeds Expectations",
          ].map((label) => (
            <div key={label} className="criteria-rate-label">
              {label}
            </div>
          ))}
        </div>
        <div className="criteria-list-scroll">
          {criteria.map((item) => (
            <div key={item.key} className="criteria-row">
              <div className="criteria-col">
                <div className="criteria-label">{item.label}</div>
                <div className="criteria-description">{item.description}</div>
              </div>
              <div className="criteria-rate-row">
                {[1, 2, 3, 4, 5].map((rate) => (
                  <button key={rate} className={`rate-btn${performance[item.key] === rate ? " selected" : ""}`} type="button">
                    <span className="rate-circle">{rate}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="white-box notes-section">
        <div className="subtitle mb-5">Observation & Advices</div>
        <div className="notes-list">
          {performance.notes && performance.notes.length > 0 ? (
            [...performance.notes]
              .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
              .map((note, idx) => {
                // Try to extract author and date info if present
                let authorFirstName = "";
                let authorLastName = "";
                let authorFullName = note.creator || "";
                if (authorFullName) {
                  const parts = authorFullName.split(" ");
                  authorFirstName = parts[0] || "";
                  authorLastName = parts.slice(1).join(" ") || "";
                }
                const userImgUrl = profileImgMap[note.creatorHRcode];
                return (
                  <Note
                    key={idx}
                    authorFirstName={authorFirstName}
                    authorLastName={authorLastName}
                    authorFullName={authorFullName}
                    date={formatDate(note.date, true)}
                    note={note.text || note.note || note}
                    type={note.type}
                    userImgUrl={userImgUrl}
                  />
                );
              })
          ) : (
            <div className="mt-15 mb-20">No notes yet.</div>
          )}
        </div>
      </div>
      {showAckModal && (
        <Modal close={() => setShowAckModal(false)} size="small" noBodyPadding={true}>
          <div className="ack-modal-content">
            <h2>Confirm Acknowledgement</h2>
            <p className="ack-modal-desc">
              Please login to confirm that you have read this Performance Review and the related Observations & Advices.
            </p>
            {ackError && <div className="alert-error mb-10">{ackError}</div>}
            <div className="ack-modal-login-row">
              <GoogleLogin
                onSuccess={async () => {
                  setAckError(null);
                  try {
                    // Call BOS backend to acknowledge
                    const res = await api.acknowledgePerformance(performance.id);
                    if (res.status === 200) {
                      setPerformance((prev) => ({
                        ...prev,
                        acknowledgedDate: res.data.acknowledgedDate || new Date().toISOString(),
                      }));
                      setAcknowledged(true);
                      setShowAckModal(false);
                    } else {
                      setAckError(res.data?.error || "Failed to acknowledge. Please try again.");
                    }
                  } catch (err) {
                    console.error("Acknowledgement error:", err);
                    setAckError("Failed to acknowledge. Please try again.");
                  }
                }}
                onError={() => setAckError("Google login failed. Please try again.")}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
